import { z } from 'zod';

import { defineRoute } from '../http/route-contract';
import { IsoDateTimeSchema } from '../primitives/datetime';
import { AccountLimitsVersionIdSchema } from '../primitives/ids';

/** Basis points of the capital: 1 bp = 0.01 %, so 1 to 10000 is 0.01 % to 100 % (C1 spec R6). */
const MaxDailyLossBpsSchema = z.int().min(1).max(10_000);

/**
 * Body of PUT /account/limits. Both keys are required: `null` disables the limit explicitly
 * (I7, FXM-02 §4), and `expectedVersion` stops a stale device overwriting a newer limit
 * (B-D16, C1 spec R18).
 */
export const UpdateAccountLimitsRequestSchema = z.strictObject({
  maxDailyLossBps: MaxDailyLossBpsSchema.nullable().describe(
    'Max daily loss on the whole account as basis points of the capital (500 = 5 %). null disables it. Basis: Q1, Q19.',
  ),
  expectedVersion: z
    .int()
    .min(1)
    .nullable()
    .describe(
      'The `version` of the limits the trader saw, or null when none was ever saved. A different current version answers 409 SETTINGS_VERSION_CONFLICT.',
    ),
});
/** PUT /account/limits body. */
export type UpdateAccountLimitsRequest = z.infer<typeof UpdateAccountLimitsRequestSchema>;

/** A new, immutable version of the account limits (master X10). */
export const AccountLimitsVersionSchema = z.object({
  id: AccountLimitsVersionIdSchema,
  version: z.int().min(1).describe('1 for the first limits ever saved, then +1 per version.'),
  createdAt: IsoDateTimeSchema,
  maxDailyLossBps: MaxDailyLossBpsSchema.nullable().describe(
    'Basis points of the capital (500 = 5 %); null when the limit is off.',
  ),
  limitAlreadyReached: z
    .boolean()
    .describe(
      "True when today's loss already exceeds the new limit: copying is suspended at once (FXM-02 §6 case 8).",
    ),
});
/** Account limits version. */
export type AccountLimitsVersion = z.infer<typeof AccountLimitsVersionSchema>;

/** PUT /account/limits. */
export const updateAccountLimitsRoute = defineRoute({
  operationId: 'updateAccountLimits',
  method: 'PUT',
  path: '/account/limits',
  tag: 'Settings',
  summary: 'Set the max daily loss of the account',
  description:
    'Creates a new limits version, never an update (FXM-02 §4, §7). The limit is a number of basis points of the capital; `expectedVersion` makes a stale write fail with 409.',
  source: 'technical spec §6; FXM-02 §4, §6 case 8; US-12; C1 spec R6, R18',
  auth: 'bearer',
  provisional: {
    questions: ['Q1', 'Q19', 'Q23', 'Q27'],
    note: 'basis, screen and currency of the limit are open.',
  },
  body: { name: 'UpdateAccountLimitsRequest', schema: UpdateAccountLimitsRequestSchema },
  success: {
    kind: 'json',
    status: 201,
    body: { name: 'AccountLimitsVersion', schema: AccountLimitsVersionSchema },
  },
  errors: [
    { status: 409, reasonCode: 'TRADING_ACCOUNT_NOT_CONNECTED' },
    { status: 409, reasonCode: 'SETTINGS_VERSION_CONFLICT' },
  ],
});
